import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  UsersIcon,
  PencilSquareIcon,
  TrashIcon,
  LockClosedIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
  BanknotesIcon,
  Bars3Icon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  MegaphoneIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { supabase, database, getAdminStatusCached, clearAdminStatusCache, notifyBookingApproved } from './lib/supabase';
import TimeSlotGrid, { formatTime as formatTimeUtil } from './TimeSlotGrid';
import Toast, { useToast } from './Toast';
import * as XLSX from 'xlsx';

function GreenAdminDashboard() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookingStatusFilter, setBookingStatusFilter] = useState('approved');
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [stats, setStats] = useState({ totalBookings: 0, totalRevenue: 0, todayBookings: 0 });
  const [memberStats, setMemberStats] = useState({ totalmembers: 0, activemembers: 0, monthlymembers: 0, yearlymembers: 0, expiredmembers: 0 });
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings', 'members', 'announcements', 'approvals', or 'slots'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingMemberships, setPendingMemberships] = useState([]);
  const [showExportFilter, setShowExportFilter] = useState(false);
  const [exportFilter, setExportFilter] = useState({ type: null, from: '', to: '' }); // type: 'today' | 'yesterday' | 'range'
  const [timeSlots, setTimeSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [editingBookingData, setEditingBookingData] = useState(null);
  const [refundBookings, setRefundBookings] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [removeSlotConfirm, setRemoveSlotConfirm] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [newSlotHour, setNewSlotHour] = useState('9');
  const [newSlotMinute, setNewSlotMinute] = useState('00');
  const [newSlotPeriod, setNewSlotPeriod] = useState('AM');
  const [newSlotType, setNewSlotType] = useState('available');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const { toast, setToast, showToast } = useToast();
  const navigate = useNavigate();

  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          navigate('/');
          return;
        }

        setUser(session.user);

        // Check if user is admin using bypass function (cached)
        const { data: profileData, error } = await getAdminStatusCached(session.user.email);
        const profile = profileData?.[0];
        if (error || !profile?.is_admin) {
          showToast('Access denied. Admin privileges required.', 'error');
          navigate('/');
          return;
        }

        setUserProfile(profile);
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    checkAdminAccess();

    // Listen for auth changes — handle sign out gracefully
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchBookings = useCallback(async () => {
    const { data, error } = await supabase
      .rpc('get_all_bookings_admin', { target_date: selectedDate });

    if (!error) {
      setBookings(data || []);
      
      // Calculate stats - only count confirmed/approved bookings
      const confirmedBookings = (data || []).filter(
        (booking) => booking.status === 'confirmed' || booking.status === 'approved'
      );

      const totalRevenue = confirmedBookings.reduce(
        (sum, booking) => sum + parseFloat(booking.total_amount || 0),
        0
      );

      // "Selected Date Bookings" card should only show bookings
      // for the current selectedDate
      const selectedDateStr = selectedDate;
      const totalBookings = confirmedBookings.filter((booking) => {
        if (!booking.booking_date) return false;
        const bookingDateStr = new Date(booking.booking_date).toISOString().split('T')[0];
        return bookingDateStr === selectedDateStr;
      }).length;

      // Today's bookings (independent of selectedDate)
      const todayStr = new Date().toISOString().split('T')[0];
      const todayBookings = confirmedBookings.filter((booking) => {
        if (!booking.booking_date) return false;
        const bookingDateStr = new Date(booking.booking_date).toISOString().split('T')[0];
        return bookingDateStr === todayStr;
      }).length;

      console.log('Stats calculated:', { totalBookings, totalRevenue, todayBookings });
      
      setStats({ totalBookings, totalRevenue, todayBookings });
    }
  }, [selectedDate]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_all_members_admin');
    if (!error) {
      setMembers(data || []);
    }
  }, []);

  // Fetch member stats
  const fetchMemberStats = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_membership_stats');
    if (!error && data?.[0]) {
      setMemberStats(data[0]);
    }
  }, []);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!error) {
        setAnnouncements(data || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  }, []);

  // Fetch pending bookings
  const fetchPendingBookings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          court_id,
          customer_name,
          customer_phone,
          customer_email,
          booking_date,
          slot_time,
          total_amount,
          created_at,
          courts!inner(name)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (!error) {
        const now = new Date();
        const bookingsToAutoReject = [];

        // Transform data and check for time-based conditions
        const transformedData = (data || []).map(booking => {
          const bookingDateTime = new Date(`${booking.booking_date}T${booking.slot_time}`);
          const createdAt = new Date(booking.created_at);

          // Check if booking is older than 48 hours
          const hoursSinceCreated = (now - createdAt) / (1000 * 60 * 60);
          const isOlderThan48Hours = hoursSinceCreated > 48;

          // Check if slot has passed with 30-minute grace period
          const slotWithGrace = new Date(bookingDateTime.getTime() + 30 * 60 * 1000);
          const isSlotPast = now > slotWithGrace;

          if (isOlderThan48Hours) {
            bookingsToAutoReject.push(booking.id);
          }

          return {
            ...booking,
            court_name: booking.courts?.name || 'Unknown Court',
            isSlotPast,
            isOlderThan48Hours
          };
        });

        // Auto-reject bookings older than 48 hours
        if (bookingsToAutoReject.length > 0) {
          await supabase
            .from('bookings')
            .update({ status: 'rejected' })
            .in('id', bookingsToAutoReject);

          // Filter out auto-rejected bookings
          const filteredData = transformedData.filter(booking => !bookingsToAutoReject.includes(booking.id));
          setPendingBookings(filteredData);
        } else {
          setPendingBookings(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching pending bookings:', error);
    }
  }, []);

  // Fetch pending memberships
  const fetchPendingMemberships = useCallback(async () => {
    try {
      // Debug current user and admin status
      const currentUser = await supabase.auth.getUser();
      console.log('🔍 Current user in fetchPendingMemberships:', currentUser.data.user);
      
      const userProfile = await supabase
        .from('users')
        .select('id, email, is_admin, full_name')
        .eq('id', currentUser.data.user?.id)
        .single();
      console.log('🔍 User profile:', userProfile);

      // Test simple query first
      const simpleTest = await supabase
        .from('memberships')
        .select('*')
        .eq('status', 'cancelled');
      console.log('🔍 Simple memberships query:', simpleTest);

      // Use RPC function to bypass RLS policy recursion issue
      const { data, error } = await supabase.rpc('get_pending_memberships_with_users');
        
      if (!error && data) {
        console.log('Raw pending memberships data (via SQL):', data);
        
        const transformedData = data.map(membership => ({
          ...membership,
          customer_name: membership.users?.full_name || 'Unknown User',
          customer_email: membership.users?.email || '',
          customer_phone: membership.users?.phone || ''
        }));
        
        console.log('Transformed pending memberships:', transformedData);
        setPendingMemberships(transformedData);
      } else {
        console.error('Error fetching pending memberships:', error);
      }
    } catch (error) {
      console.error('Error fetching pending memberships:', error);
    }
  }, []);

  // Fetch time slots (Slots tab)
  const fetchTimeSlots = useCallback(async () => {
    setSlotsLoading(true);
    try {
      const { data, error } = await supabase
        .from('time_slots')
        .select('*')
        .order('sort_order', { ascending: true });
      if (!error) {
        setTimeSlots(data || []);
      } else {
        console.error('Error fetching time slots:', error);
        setTimeSlots([]);
      }
    } catch (err) {
      console.error('Fetch time slots error:', err);
      setTimeSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const buildTime24 = (hour, minute, period) => {
    let h = parseInt(hour, 10);
    if (period === 'AM' && h === 12) h = 0;
    else if (period === 'PM' && h !== 12) h += 12;
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  const handleAddSlot = async () => {
    const time24 = buildTime24(newSlotHour, newSlotMinute, newSlotPeriod);
    const exists = timeSlots.some((s) => (s.slot_time || '').substring(0, 5) === time24);
    if (exists) {
      showToast('This time slot already exists.', 'error');
      return;
    }
    try {
      const maxOrder = timeSlots.length ? Math.max(...timeSlots.map(s => s.sort_order || 0)) : 0;
      const { error } = await supabase
        .from('time_slots')
        .insert([{ slot_time: time24, slot_type: newSlotType, is_active: true, sort_order: maxOrder + 1 }]);
      if (error) {
        console.error('Add slot error:', error);
        showToast('Failed to add slot: ' + (error.message || error.details), 'error');
        return;
      }
      showToast('Time slot added successfully!', 'success');
      setShowAddSlotModal(false);
      setNewSlotHour('9');
      setNewSlotMinute('00');
      setNewSlotPeriod('AM');
      setNewSlotType('available');
      fetchTimeSlots();
    } catch (err) {
      console.error('Add slot error:', err);
      showToast('Failed to add slot: ' + err.message, 'error');
    }
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot) return;
    try {
      const { error } = await supabase
        .from('time_slots')
        .update({ slot_type: newSlotType })
        .eq('id', editingSlot.id);
      if (error) {
        showToast('Failed to update slot: ' + (error.message || error.details), 'error');
        return;
      }
      showToast('Time slot updated!', 'success');
      setEditingSlot(null);
      setNewSlotType('available');
      fetchTimeSlots();
    } catch (err) {
      console.error('Update slot error:', err);
      showToast('Failed to update slot: ' + err.message, 'error');
    }
  };

  const handleRemoveSlot = (slot) => {
    setRemoveSlotConfirm(slot);
  };

  const confirmRemoveSlot = async () => {
    const slot = removeSlotConfirm;
    setRemoveSlotConfirm(null);
    if (!slot) return;
    try {
      const { error } = await supabase.from('time_slots').delete().eq('id', slot.id);
      if (error) {
        showToast('Failed to remove slot: ' + (error.message || error.details), 'error');
        return;
      }
      showToast('Time slot removed!', 'success');
      fetchTimeSlots();
    } catch (err) {
      console.error('Remove slot error:', err);
      showToast('Failed to remove slot: ' + err.message, 'error');
    }
  };

  const handleToggleSlotActive = async (slot) => {
    try {
      const { error } = await supabase
        .from('time_slots')
        .update({ is_active: !slot.is_active })
        .eq('id', slot.id);
      if (error) {
        showToast('Failed to toggle slot: ' + (error.message || error.details), 'error');
        return;
      }
      setTimeSlots(prev => prev.map(s => s.id === slot.id ? { ...s, is_active: !s.is_active } : s));
    } catch (err) {
      console.error('Toggle slot error:', err);
      showToast('Failed to toggle slot: ' + err.message, 'error');
    }
  };

  const formatSlotTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${(m || 0).toString().padStart(2, '0')} ${ampm}`;
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      if (activeTab === 'bookings') {
        const { data, error } = await supabase
          .rpc('search_bookings_admin', { 
            search_term: searchTerm.trim(),
            target_date: selectedDate 
          });
        if (!error) {
          setSearchResults(data || []);
        }
      } else {
        const { data, error } = await supabase
          .rpc('search_members_admin', { search_term: searchTerm.trim() });
        if (!error) {
          setSearchResults(data || []);
        }
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
  };

  // Export functionality
  const exportToExcel = (data, filename) => {
    if (!data || data.length === 0) {
      showToast('No data to export', 'error');
      return;
    }

    let worksheetData = [];
    
    if (activeTab === 'bookings') {
      // Headers
      worksheetData.push(['Customer Name', 'Phone', 'Email', 'Time Slots', 'Amount', 'Status', 'Membership', 'Booking Date', 'Created At']);
      
      // Data rows
      data.forEach(booking => {
        const membershipText = booking.membership_type ? 
          `${booking.membership_status} (${booking.membership_type})` : 
          'No Membership';
          
        worksheetData.push([
          booking.customer_name || '',
          booking.customer_phone || '',
          booking.customer_email || '',
          booking.time_slots || '',
          booking.total_amount || '',
          booking.status || '',
          membershipText,
          booking.booking_date || '',
          new Date(booking.created_at).toLocaleString()
        ]);
      });
    } else {
      // Headers
      worksheetData.push(['Full Name', 'Phone', 'Email', 'Membership Type', 'Start Date', 'End Date', 'Status', 'Days Remaining', 'Joined']);
      
      // Data rows
      data.forEach(member => {
        worksheetData.push([
          member.full_name || '',
          member.phone || '',
          member.email || '',
          member.membership_type || '',
          member.start_date || '',
          member.end_date || '',
          member.status || '',
          member.days_remaining || '',
          new Date(member.created_at).toLocaleDateString()
        ]);
      });
    }

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Auto-size columns
    const colWidths = worksheetData[0].map((_, colIndex) => {
      const maxLength = Math.max(
        ...worksheetData.map(row => (row[colIndex] || '').toString().length)
      );
      return { wch: Math.min(Math.max(maxLength, 10), 50) };
    });
    worksheet['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, activeTab === 'bookings' ? 'Bookings' : 'Members');
    
    // Download file
    XLSX.writeFile(workbook, filename);
  };

  // Helper: get list of dates between fromDate and toDate (inclusive)
  const getDateRange = (fromDate, toDate) => {
    const dates = [];
    let current = new Date(fromDate);
    const end = new Date(toDate);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() - 0 + 1); // increment by 1 day
    }
    return dates;
  };

  // Fetch bookings for a date range using existing RPC
  const fetchBookingsForExportRange = async (fromDate, toDate) => {
    const dates = getDateRange(fromDate, toDate);
    const all = [];

    for (const d of dates) {
      const { data, error } = await supabase
        .rpc('get_all_bookings_admin', { target_date: d });
      if (error) {
        console.error('Export bookings fetch error for date', d, error);
        continue;
      }
      if (data && Array.isArray(data)) {
        all.push(...data);
      }
    }
    return all;
  };

  const handleExport = async (filter) => {
    const effectiveFilter = filter || exportFilter;

    if (!effectiveFilter.type) {
      showToast('Please choose a date filter before exporting.', 'error');
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let fromDate;
    let toDate;

    if (effectiveFilter.type === 'today') {
      fromDate = todayStr;
      toDate = todayStr;
    } else if (effectiveFilter.type === 'yesterday') {
      fromDate = yesterdayStr;
      toDate = yesterdayStr;
    } else {
      fromDate = effectiveFilter.from;
      toDate = effectiveFilter.to;
    }

    if (!fromDate || !toDate) {
      showToast('Please select valid From and To dates.', 'error');
      return;
    }

    let dataToExport = [];
    let label = '';

    if (activeTab === 'bookings') {
      // Always fetch fresh bookings for the whole range
      dataToExport = await fetchBookingsForExportRange(fromDate, toDate);
      label = effectiveFilter.type === 'range' ? `${fromDate}_to_${toDate}` : fromDate;
    } else {
      // Members: use existing list and filter by created_at
      const baseData = isSearching ? searchResults : members;
      dataToExport = baseData.filter((member) => {
        if (!member.created_at) return false;
        const dateStr = new Date(member.created_at).toISOString().split('T')[0];
        return dateStr >= fromDate && dateStr <= toDate;
      });
      label = effectiveFilter.type === 'range' ? `${fromDate}_to_${toDate}` : fromDate;
    }

    if (!dataToExport || dataToExport.length === 0) {
      showToast('No data found for the selected date(s).', 'error');
      return;
    }

    const filename = `${activeTab}_${label}.xlsx`;
    exportToExcel(dataToExport, filename);
  };

  const fetchCourts = useCallback(async () => {
    const { data, error } = await supabase
      .from('courts')
      .select('*');

    if (!error) {
      setCourts(data || []);
    }
  }, []);

  const fetchRefundBookings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          court_id,
          customer_name,
          customer_phone,
          customer_email,
          booking_date,
          slot_time,
          total_amount,
          status,
          refund_status,
          created_at,
          courts!inner(name)
        `)
        .eq('status', 'cancelled')
        .eq('refund_status', 'pending')
        .order('created_at', { ascending: false });

      if (!error) {
        const transformed = (data || []).map(b => ({
          ...b,
          court_name: b.courts?.name || 'Unknown Court'
        }));
        setRefundBookings(transformed);
      }
    } catch (error) {
      console.error('Error fetching refund bookings:', error);
    }
  }, []);

  useEffect(() => {
    if (!loading && userProfile?.is_admin) {
      fetchBookings();
    }
  }, [loading, userProfile, fetchBookings]);

  useEffect(() => {
    if (!loading && userProfile?.is_admin) {
      fetchCourts();
      fetchMembers();
      fetchMemberStats();
      fetchAnnouncements();
      fetchPendingBookings();
      fetchPendingMemberships();
      fetchTimeSlots();
      fetchRefundBookings();
    }
  }, [loading, userProfile, fetchCourts, fetchMembers, fetchMemberStats, fetchAnnouncements, fetchPendingBookings, fetchPendingMemberships, fetchTimeSlots, fetchRefundBookings]);

  const isBookingPast = (booking) => {
    if (booking.isSlotPast !== undefined) return booking.isSlotPast;
    if (!booking.booking_date) return false;

    const now = new Date();
    const bookingDateStr = new Date(booking.booking_date).toISOString().split('T')[0];
    const todayStr = now.toISOString().split('T')[0];

    if (bookingDateStr < todayStr) return true;

    if (bookingDateStr > todayStr) return false;

    // Date is today — check slot time
    let slotTimeStr = booking.slot_time;
    if (!slotTimeStr && booking.time_slots) {
      const parts = booking.time_slots.split(',');
      const last = parts[parts.length - 1].trim();
      const match = last.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (match) {
        let h = parseInt(match[1], 10);
        const m = match[2];
        const p = match[3].toUpperCase();
        if (p === 'AM' && h === 12) h = 0;
        else if (p === 'PM' && h !== 12) h += 12;
        slotTimeStr = `${h.toString().padStart(2, '0')}:${m}:00`;
      }
    }
    if (!slotTimeStr) return false;

    const slotEnd = new Date(`${bookingDateStr}T${slotTimeStr}`);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);
    return now > slotEnd;
  };

  const cancelBooking = (bookingId) => {
    if (!bookingId) {
      showToast('Invalid booking ID', 'error');
      return;
    }
    setCancelConfirm({ bookingId });
  };

  const confirmCancelBooking = async (isRefundable) => {
    const bookingId = cancelConfirm?.bookingId;
    setCancelConfirm(null);
    if (!bookingId) return;

    try {
      const { data, error } = await supabase
        .from('bookings')
        .update({
          status: 'cancelled',
          refund_status: isRefundable ? 'pending' : null
        })
        .eq('id', bookingId)
        .in('status', ['confirmed', 'approved'])
        .select('customer_name')
        .single();

      if (error) {
        console.error('Cancel error:', error);
        showToast('Failed to cancel booking: ' + error.message, 'error');
        return;
      }

      if (data) {
        showToast(`Booking cancelled successfully: ${data.customer_name}${isRefundable ? ' (Refund pending)' : ''}`, 'success');
        fetchBookings();
        fetchRefundBookings();
      } else {
        showToast('Booking not found or already cancelled', 'error');
      }
    } catch (err) {
      console.error('Cancel booking error:', err);
      showToast('Failed to cancel booking: ' + err.message, 'error');
    }
  };

  const markRefunded = async (bookingId) => {
    if (!window.confirm('Mark this booking as refunded?')) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({ refund_status: 'refunded' })
        .eq('id', bookingId);

      if (error) throw error;

      showToast('Booking marked as refunded!', 'success');
      fetchRefundBookings();
    } catch (err) {
      console.error('Refund error:', err);
      showToast('Failed to mark as refunded: ' + err.message, 'error');
    }
  };

  const updateBookingStatus = (bookingId, newStatus) => {
    setStatusConfirm({ bookingId, newStatus });
  };

  const confirmStatusUpdate = async () => {
    const { bookingId, newStatus } = statusConfirm || {};
    setStatusConfirm(null);
    if (!bookingId) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: newStatus })
        .eq('id', bookingId);

      if (error) throw error;

      fetchBookings();
      fetchRefundBookings();
    } catch (err) {
      console.error('Status update error:', err);
      showToast('Failed to update status: ' + err.message, 'error');
    }
  };

  // Announcement management functions
  const createAnnouncement = async () => {
    if (!newAnnouncement.trim()) {
      showToast('Please enter announcement content', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('announcements')
        .insert([
          {
            content: newAnnouncement.trim(),
            is_active: true,
            created_by: user?.id || null
          }
        ]);

      if (error) throw error;

      showToast('Announcement created successfully!', 'success');
      setNewAnnouncement('');
      setShowCreateAnnouncement(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Create announcement error:', error);
      showToast('Failed to create announcement: ' + error.message, 'error');
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', announcementId);

      if (error) throw error;

      showToast('Announcement deleted successfully!', 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Delete announcement error:', error);
      showToast('Failed to delete announcement: ' + error.message, 'error');
    }
  };

  // Approve booking
  const approveBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to approve this booking?')) {
      return;
    }

    try {
      // Find the booking to get details
      const booking = pendingBookings.find(b => b.id === bookingId);
      
      if (!booking) {
        showToast('Booking not found', 'error');
        return;
      }

      setPendingBookings(prev => prev.filter(b => b.id !== bookingId));

      const { data, error } = await supabase
        .rpc('approve_booking_with_conflicts', { booking_id: bookingId });

      if (error) throw error;

      const result = data || {};
      if (result.success) {
        let message = `Booking approved for ${result.customer_name}!`;
        if (result.rejected_conflicts > 0) {
          message += ` (${result.rejected_conflicts} conflicting booking(s) auto-rejected)`;
        }
        showToast(message, 'success');
        notifyBookingApproved({ booking_id: bookingId });
        fetchBookings();
        fetchPendingBookings();
      } else {
        showToast(result.message, 'error');
        if (booking) {
          setPendingBookings(prev => [...prev, booking]);
        }
      }
    } catch (error) {
      console.error('Approve booking error:', error);
      showToast('Failed to approve booking: ' + error.message, 'error');
      // Refresh pending bookings on error
      fetchPendingBookings();
    }
  };

  // Reject booking
  const rejectBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to reject this booking?')) {
      return;
    }

    try {
      // Find the booking to get details
      const booking = pendingBookings.find(b => b.id === bookingId);
      
      if (!booking) {
        showToast('Booking not found', 'error');
        return;
      }

      setPendingBookings(prev => prev.filter(b => b.id !== bookingId));

      const { data, error } = await supabase
        .rpc('reject_booking', { booking_id: bookingId });

      if (error) throw error;

      const result = data || {};
      if (result.success) {
        showToast(result.message || `Booking rejected for ${booking.customer_name}`, 'success');
      } else {
        showToast(result.message || 'Failed to reject booking', 'error');
      }
      fetchBookings();
      
    } catch (error) {
      console.error('Reject booking error:', error);
      showToast('Failed to reject booking: ' + error.message, 'error');
      // Refresh pending bookings on error
      fetchPendingBookings();
    }
  };

  // Approve membership
  const approveMembership = async (membershipId) => {
    if (!window.confirm('Are you sure you want to approve this membership?')) {
      return;
    }

    try {
      // Find the membership to get details
      const membership = pendingMemberships.find(m => m.id === membershipId);
      
      if (!membership) {
        showToast('Membership not found', 'error');
        return;
      }

      setPendingMemberships(prev => prev.filter(m => m.id !== membershipId));

      const { error } = await supabase
        .rpc('approve_membership', { membership_id: membershipId });

      if (error) throw error;

      showToast(`Membership approved for ${membership.full_name}!`, 'success');
      fetchMembers();
      
    } catch (error) {
      console.error('Approve membership error:', error);
      showToast('Failed to approve membership: ' + error.message, 'error');
      // Refresh pending memberships on error
      fetchPendingMemberships();
    }
  };

  // Reject membership
  const rejectMembership = async (membershipId) => {
    if (!window.confirm('Are you sure you want to reject this membership?')) {
      return;
    }

    try {
      // Find the membership to get details
      const membership = pendingMemberships.find(m => m.id === membershipId);
      
      if (!membership) {
        showToast('Membership not found', 'error');
        return;
      }

      setPendingMemberships(prev => prev.filter(m => m.id !== membershipId));

      const { error } = await supabase
        .rpc('reject_membership', { membership_id: membershipId });

      if (error) throw error;

      showToast(`Membership rejected for ${membership.full_name}`, 'success');
      fetchMembers();
      
    } catch (error) {
      console.error('Reject membership error:', error);
      showToast('Failed to reject membership: ' + error.message, 'error');
      // Refresh pending memberships on error
      fetchPendingMemberships();
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) { /* silent */ }
    clearAdminStatusCache();
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) { /* silent */ }
    window.location.replace('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-30 bg-white border-r border-gray-200 shadow-lg flex flex-col transition-transform duration-300 w-64 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'}`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-200">
          <img 
            src="/images/SquareLogo.jpg" 
            alt="Best Badminton Club" 
            className="h-10 w-10 rounded-lg object-contain flex-shrink-0"
          />
          <div className="min-w-0 lg:hidden block">
            <h1 className="text-lg font-bold text-gray-900 truncate">Admin Panel</h1>
            <p className="text-xs text-gray-500 truncate">Best Badminton Club</p>
          </div>
          {sidebarOpen && (
            <div className="min-w-0 hidden lg:block">
              <h1 className="text-lg font-bold text-gray-900 truncate">Admin Panel</h1>
              <p className="text-xs text-gray-500 truncate">Best Badminton Club</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto p-1 rounded-lg text-gray-400 hover:text-gray-600 lg:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {[
            { id: 'bookings', label: 'Bookings', icon: CalendarIcon },
            { id: 'members', label: 'Members', icon: UsersIcon },
            { id: 'announcements', label: 'Announcements', icon: MegaphoneIcon },
            { id: 'approvals', label: 'Approvals', icon: CheckCircleIcon, badge: pendingBookings.length + pendingMemberships.length },
            { id: 'slots', label: 'Slots', icon: ClockIcon },
            { id: 'refunds', label: 'Refunds', icon: ArrowPathIcon, badge: refundBookings.filter(b => b.refund_status !== 'refunded').length },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative ${
                activeTab === item.id
                  ? 'bg-green-50 text-green-700 border-l-[3px] border-green-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title={!sidebarOpen ? item.label : undefined}
            >
              <item.icon className={`h-5 w-5 flex-shrink-0 ${activeTab === item.id ? 'text-green-600' : 'text-gray-400'}`} />
              <span className="flex-1 text-left lg:hidden block">{item.label}</span>
              {sidebarOpen && (
                <span className="flex-1 text-left hidden lg:block">{item.label}</span>
              )}
              {item.badge > 0 && (
                <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="border-t border-gray-200 p-3 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <HomeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <span className="lg:hidden block">Back to Home</span>
            {sidebarOpen && <span className="hidden lg:block">Back to Home</span>}
          </Link>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <span className="lg:hidden block">Sign Out</span>
            {sidebarOpen && <span className="hidden lg:block">Sign Out</span>}
          </button>
          <div className="px-3 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400 truncate lg:hidden block">{userProfile?.full_name || user?.email}</p>
            {sidebarOpen && (
              <p className="text-xs text-gray-400 truncate hidden lg:block">{userProfile?.full_name || user?.email}</p>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} ml-0`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 flex-shrink-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
                {activeTab === 'bookings' ? 'Bookings' :
                 activeTab === 'members' ? 'Members' :
                 activeTab === 'announcements' ? 'Announcements' :
                 activeTab === 'approvals' ? 'Approvals' :
                 activeTab === 'slots' ? 'Slots' :
                 activeTab === 'refunds' ? 'Refunds' : ''}
              </h2>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-sm text-gray-500">
              <PhoneIcon className="h-4 w-4" />
              <span>+91 8080517517</span>
              <span className="text-gray-300">|</span>
              <ClockIcon className="h-4 w-4" />
              <span>5:00 AM - 10:00 PM</span>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden lg:overflow-hidden lg:flex lg:flex-col">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-6 lg:flex-shrink-0">
          {activeTab === 'bookings' ? (
            <>
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Today</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900">{stats.todayBookings}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Revenue</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900 truncate">₹{stats.totalRevenue.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <UserGroupIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Selected Date</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900">{stats.totalBookings}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Export</p>
                    <button 
                      onClick={() => setShowExportFilter(true)}
                      className="text-xs sm:text-sm text-green-600 hover:text-green-700 font-semibold"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : activeTab === 'members' ? (
            <>
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Total</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900">{memberStats.totalmembers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Active</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900">{memberStats.activemembers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Monthly/Yearly</p>
                    <p className="text-base sm:text-xl font-bold text-gray-900">{memberStats.monthlymembers}/{memberStats.yearlymembers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-2.5 sm:p-5 border border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 rounded-md p-1.5 sm:p-2.5 flex-shrink-0">
                    <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Export</p>
                    <button
                      onClick={() => setShowExportFilter(true)}
                      className="text-xs sm:text-sm text-green-600 hover:text-green-700 font-semibold"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Search and Controls - only for Bookings and Members */}
        {(activeTab === 'bookings' || activeTab === 'members') && (
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-3 sm:mb-6 border border-gray-200 lg:flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-end gap-2 sm:gap-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 sm:gap-3 flex-1">
              {/* Search Box */}
              <div className="relative flex-1 sm:max-w-xs">
                <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                <div className="relative flex">
                  <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={activeTab === 'bookings' ? 'Search bookings...' : 'Search members...'}
                    className="pl-8 pr-20 py-1.5 w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <div className="absolute right-1.5 top-1/2 transform -translate-y-1/2 flex space-x-1">
                    <button
                      onClick={handleSearch}
                      className="px-2.5 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      Go
                    </button>
                    {(searchTerm || isSearching) && (
                      <button
                        onClick={clearSearch}
                        className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Date Filter - Only for bookings */}
              {activeTab === 'bookings' && (
                <>
                  <div className="w-full sm:w-auto min-w-0 flex flex-col">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Date</label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="pl-8 pr-3 py-1.5 text-sm w-full min-w-[140px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent h-[34px]"
                      />
                    </div>
                  </div>
                  <div className="w-full sm:w-auto min-w-0 flex flex-col">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <div className="relative">
                      <select
                        value={bookingStatusFilter}
                        onChange={(e) => setBookingStatusFilter(e.target.value)}
                        className="w-full min-w-[120px] pl-3 pr-10 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white h-[34px] appearance-none"
                      >
                        <option value="approved">Approved</option>
                        <option value="all">All</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="pending">Pending</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none stroke-[2.5]" />
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Action Buttons */}
            {activeTab === 'bookings' && selectedDate >= new Date().toISOString().split('T')[0] && (
              <button
                onClick={() => setShowCreateBooking(true)}
                className="flex items-center px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors justify-center whitespace-nowrap"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Create Booking
              </button>
            )}
          </div>
        </div>
        )}

        {/* Content Area */}
        {activeTab === 'bookings' ? (
          /* Bookings Table */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[45vh] sm:max-h-[50vh] lg:max-h-none lg:flex-1 min-h-0 overflow-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                {isSearching ? `Search Results (${searchResults.length})` : `Bookings for ${formatDate(selectedDate)}`}
              </h2>
            </div>

            {(() => {
              const baseList = isSearching ? searchResults : bookings;
              const filteredBookings = bookingStatusFilter === 'all'
                ? baseList
                : bookingStatusFilter === 'approved'
                  ? baseList.filter((b) => b.status === 'approved' || b.status === 'confirmed')
                  : baseList.filter((b) => b.status === bookingStatusFilter);
              return filteredBookings.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <CalendarIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {isSearching ? 'No search results found' : 'No bookings found'}
                </h3>
                <p className="text-sm sm:text-base text-gray-500">
                  {isSearching ? 'Try different search terms' : bookingStatusFilter === 'all' ? 'No bookings scheduled for this date' : `No ${bookingStatusFilter} bookings for this date`}
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full table-fixed min-w-[520px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                        Customer
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                        Contact
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                        Time
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">
                        Amt
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                        Status
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">
                        Member
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                        Booked
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="truncate">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                              {booking.customer_name}
                            </div>
                            {booking.user_email && (
                              <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                                {booking.user_email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900">
                            {booking.customer_phone}
                          </div>
                          {booking.customer_email && (
                            <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                              {booking.customer_email}
                            </div>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900 break-words">
                            {booking.time_slots || (booking.slot_time ? formatSlotTime(booking.slot_time) : '-')}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm font-semibold text-gray-900">
                            ₹{booking.total_amount}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            booking.status === 'confirmed' || booking.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {booking.status === 'confirmed' || booking.status === 'approved' ? (
                              <CheckCircleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            ) : booking.status === 'pending' ? (
                              <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            ) : (
                              <ExclamationTriangleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            )}
                            {booking.status === 'approved' ? 'Approved' : 
                             booking.status === 'cancelled' ? 'Cancelled' : 
                             booking.status === 'rejected' ? 'Rejected' :
                             booking.status}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            booking.has_membership && booking.membership_status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : booking.membership_status === 'Expired'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.has_membership && booking.membership_status === 'Active' ? (
                              <CheckCircleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            ) : booking.membership_status === 'Expired' ? (
                              <ExclamationTriangleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            ) : (
                              <span className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1 rounded-full bg-gray-400 inline-block"></span>
                            )}
                            {booking.membership_type ? 
                              `${booking.membership_status} (${booking.membership_type})` : 
                              'None'
                            }
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 text-[10px] sm:text-xs text-gray-500">
                          {new Date(booking.created_at).toLocaleString()}
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="flex items-center gap-1">
                            {(booking.status === 'confirmed' || booking.status === 'approved') && !isBookingPast(booking) && (
                              <>
                                <button
                                  onClick={() => setEditingBookingData(booking)}
                                  className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                  title="Edit Booking"
                                >
                                  <PencilSquareIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => cancelBooking(booking.booking_id || booking.id)}
                                  className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                  title="Cancel Booking"
                                >
                                  <XMarkIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                  Cancel
                                </button>
                              </>
                            )}
                            {(booking.status === 'cancelled' || booking.status === 'rejected') && !isBookingPast(booking) && (
                              <button
                                onClick={() => updateBookingStatus(booking.booking_id || booking.id, 'approved')}
                                className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                title="Approve Booking"
                              >
                                <CheckCircleIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                Approve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            })()}
          </div>
        ) : activeTab === 'members' ? (
          /* Members Table */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[45vh] sm:max-h-[50vh] lg:max-h-none lg:flex-1 min-h-0 overflow-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                {isSearching ? `Search Results (${searchResults.length})` : `Members (${members.length})`}
              </h2>
            </div>

            {(isSearching ? searchResults : members).length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <UsersIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {isSearching ? 'No search results found' : 'No members found'}
                </h3>
                <p className="text-sm sm:text-base text-gray-500">
                  {isSearching ? 'Try different search terms' : 'No members registered yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full table-fixed min-w-[520px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">
                        Member
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">
                        Contact
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                        Type
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">
                        Duration
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                        Status
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                        Remaining
                      </th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(isSearching ? searchResults : members).map((member) => (
                      <tr key={member.user_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="truncate">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                              {member.full_name}
                            </div>
                            <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                              ID: {member.user_id.slice(0, 8)}...
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900">
                            {member.phone}
                          </div>
                          {member.email && (
                            <div className="text-[10px] sm:text-xs text-gray-500 truncate">
                              {member.email}
                            </div>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            member.membership_type === 'yearly'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {member.membership_type}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">
                          <div>
                            <div>{new Date(member.start_date).toLocaleDateString()}</div>
                            <div className="text-[10px] sm:text-xs text-gray-500">to {new Date(member.end_date).toLocaleDateString()}</div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            member.status === 'active' && member.days_remaining > 0
                              ? 'bg-green-100 text-green-800'
                              : member.days_remaining <= 7 && member.days_remaining > 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {member.status === 'active' && member.days_remaining > 0
                              ? <CheckCircleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                              : <ExclamationTriangleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            }
                            {member.status === 'active' && member.days_remaining > 0
                              ? 'Active'
                              : member.days_remaining <= 7 && member.days_remaining > 0
                              ? 'Expiring'
                              : 'Expired'
                            }
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">
                          <span className={member.days_remaining <= 7 ? 'text-red-600 font-semibold' : ''}>
                            {member.days_remaining > 0 ? `${member.days_remaining}d` : 'Expired'}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 text-[10px] sm:text-xs text-gray-500">
                          {new Date(member.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === 'announcements' ? (
          /* Announcements Management */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col lg:flex-1 min-h-0 min-w-0 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                  Announcements ({announcements.length})
                </h2>
                <button
                  onClick={() => setShowCreateAnnouncement(true)}
                  className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors w-full sm:w-auto justify-center"
                >
                  <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  Create
                </button>
              </div>
            </div>

            {announcements.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <ExclamationTriangleIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  No announcements found
                </h3>
                <p className="text-sm sm:text-base text-gray-500">
                  Create your first announcement to display on the homepage
                </p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4 p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-gray-900 mb-2">{announcement.content}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Created: {new Date(announcement.created_at).toLocaleDateString()}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            announcement.is_active 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {announcement.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteAnnouncement(announcement.id)}
                        className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'approvals' ? (
          /* Approvals Management */
          <div className="space-y-4 sm:space-y-6 lg:flex-1 min-h-0 overflow-y-auto overflow-x-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
            {/* Pending Bookings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[45vh] sm:max-h-[50vh] lg:max-h-none min-h-0 overflow-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                  Pending Bookings ({pendingBookings.length})
                </h2>
              </div>

              {pendingBookings.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <CalendarIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No pending bookings</h3>
                  <p className="text-sm sm:text-base text-gray-500">All bookings have been processed</p>
                </div>
              ) : (
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="w-full table-fixed min-w-[520px]">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">Customer</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Contact</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Court</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Date</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Time</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Amt</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Status</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingBookings.map((booking) => (
                        <tr key={booking.id} className={`transition-colors ${booking.isSlotPast ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{booking.customer_name}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900">{booking.customer_phone}</div>
                            {booking.customer_email && (
                              <div className="text-[10px] sm:text-xs text-gray-500 truncate">{booking.customer_email}</div>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900 truncate">{booking.court_name}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900">{new Date(booking.booking_date).toLocaleDateString()}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900">{booking.slot_time ? formatSlotTime(booking.slot_time) : '-'}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm font-semibold text-gray-900">₹{booking.total_amount}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                              booking.isSlotPast ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.isSlotPast ? (
                                <ExclamationTriangleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                              ) : (
                                <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                              )}
                              {booking.isSlotPast ? 'Past' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => approveBooking(booking.id)}
                                disabled={booking.isSlotPast}
                                className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium transition-colors ${
                                  booking.isSlotPast
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                                }`}
                              >
                                <CheckCircleIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => rejectBooking(booking.id)}
                                className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                              >
                                <XMarkIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pending Memberships */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[45vh] sm:max-h-[50vh] lg:max-h-none min-h-0 overflow-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                  Pending Memberships ({pendingMemberships.length})
                </h2>
              </div>

              {pendingMemberships.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <UsersIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No pending memberships</h3>
                  <p className="text-sm sm:text-base text-gray-500">All membership requests have been processed</p>
                </div>
              ) : (
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="w-full table-fixed min-w-[520px]">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">Name</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">Email</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Phone</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Type</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Amt</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Date</th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingMemberships.map((membership) => (
                        <tr key={membership.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{membership.full_name}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900 truncate">{membership.email}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm text-gray-900">{membership.phone}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                              membership.membership_type === 'yearly'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {membership.membership_type}
                            </span>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="text-xs sm:text-sm font-semibold text-gray-900">₹{membership.amount_paid}</div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3 text-[10px] sm:text-xs text-gray-500">
                            {new Date(membership.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => approveMembership(membership.id)}
                                className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                              >
                                <CheckCircleIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => rejectMembership(membership.id)}
                                className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                              >
                                <XMarkIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'slots' ? (
          /* Slots Management */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col lg:flex-1 min-h-0 min-w-0 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">Time Slots</h2>
              <button
                onClick={() => {
                  setShowAddSlotModal(true);
                  setNewSlotHour('9');
                  setNewSlotMinute('00');
                  setNewSlotPeriod('AM');
                  setNewSlotType('available');
                }}
                className="inline-flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Add
              </button>
            </div>
            {slotsLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-green-500 border-t-transparent mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading slots...</p>
              </div>
            ) : timeSlots.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No time slots configured. Run the migration in Supabase (supabase/migrations/001_create_time_slots.sql) or click Add to create slots.</p>
              </div>
            ) : (
              <div className="p-3 sm:p-4">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-1.5 sm:gap-2">
                  {timeSlots.map((slot) => (
                    <div
                      key={slot.id}
                      className={`group relative rounded-xl overflow-hidden transition-all duration-200 ${
                        slot.is_active === false
                          ? 'opacity-60 ring-1 ring-gray-200 bg-gray-50'
                          : 'ring-1 hover:ring-2 hover:shadow-md ' + (
                              slot.slot_type === 'members_only'
                                ? 'ring-amber-200 hover:ring-amber-300 bg-white'
                                : slot.slot_type === 'coaching_hour'
                                ? 'ring-purple-200 hover:ring-purple-300 bg-white'
                                : 'ring-gray-200 hover:ring-green-300 bg-white'
                            )
                      }`}
                    >
                      {/* Color accent bar */}
                      <div className={`h-1 w-full ${
                        slot.is_active === false ? 'bg-gray-300' :
                        slot.slot_type === 'members_only' ? 'bg-amber-400' :
                        slot.slot_type === 'coaching_hour' ? 'bg-purple-400' :
                        'bg-green-400'
                      }`} />

                      <div className="px-1.5 sm:px-3 py-2 sm:py-2.5 flex flex-col items-center">
                        {/* Time */}
                        <div className="font-semibold text-xs sm:text-sm text-gray-800 truncate w-full text-center">{formatSlotTime(slot.slot_time)}</div>

                        {/* Type badge */}
                        <span className={`inline-flex items-center gap-0.5 sm:gap-1 mt-1 sm:mt-1.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-medium ${
                          slot.slot_type === 'members_only'
                            ? 'bg-amber-100 text-amber-700'
                            : slot.slot_type === 'coaching_hour'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {slot.slot_type === 'members_only' && <LockClosedIcon className="h-2 w-2 sm:h-2.5 sm:w-2.5" />}
                          {slot.slot_type === 'coaching_hour' && <BuildingOffice2Icon className="h-2 w-2 sm:h-2.5 sm:w-2.5" />}
                          {slot.slot_type === 'members_only' ? 'Members' :
                           slot.slot_type === 'coaching_hour' ? 'Coaching' : 'Open'}
                        </span>

                        {/* Toggle */}
                        <div className="flex items-center mt-1.5 sm:mt-2">
                          <button
                            onClick={() => handleToggleSlotActive(slot)}
                            className={`relative inline-flex h-[16px] sm:h-[18px] w-7 sm:w-8 items-center rounded-full transition-colors focus:outline-none ${
                              slot.is_active !== false ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={slot.is_active !== false ? 'Active – click to freeze' : 'Frozen – click to unfreeze'}
                          >
                            <span
                              className={`inline-block h-2.5 sm:h-3 w-2.5 sm:w-3 transform rounded-full bg-white shadow transition-transform ${
                                slot.is_active !== false ? 'translate-x-[12px] sm:translate-x-[14px]' : 'translate-x-[3px]'
                              }`}
                            />
                          </button>
                          <span className={`ml-1 sm:ml-1.5 text-[9px] sm:text-[10px] font-medium ${slot.is_active !== false ? 'text-green-600' : 'text-gray-400'}`}>
                            {slot.is_active !== false ? 'Active' : 'Frozen'}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-center gap-0.5 sm:gap-1 mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t border-gray-100 w-full">
                          <button
                            onClick={() => {
                              setEditingSlot(slot);
                              setNewSlotType(slot.slot_type);
                            }}
                            className="flex-1 flex items-center justify-center gap-0.5 sm:gap-1 py-1 rounded-md text-[10px] sm:text-[11px] font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-3 w-3 flex-shrink-0" />
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          <div className="w-px bg-gray-100" />
                          <button
                            onClick={() => handleRemoveSlot(slot)}
                            className="flex-1 flex items-center justify-center gap-0.5 sm:gap-1 py-1 rounded-md text-[10px] sm:text-[11px] font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Remove"
                          >
                            <TrashIcon className="h-3 w-3 flex-shrink-0" />
                            <span className="hidden sm:inline">Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'refunds' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[45vh] sm:max-h-[50vh] lg:max-h-none lg:flex-1 min-h-0 overflow-hidden" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">
                Refunds ({refundBookings.filter(b => b.refund_status !== 'refunded').length} pending)
              </h2>
            </div>

            {refundBookings.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <ArrowPathIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No refunds pending</h3>
                <p className="text-sm sm:text-base text-gray-500">Cancelled paid bookings will appear here</p>
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full table-fixed min-w-[520px]">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">Customer</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Contact</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[13%]">Date</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Time</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Amt</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Status</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Cancelled</th>
                      <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {refundBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{booking.customer_name}</div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900">{booking.customer_phone}</div>
                          {booking.customer_email && (
                            <div className="text-[10px] sm:text-xs text-gray-500 truncate">{booking.customer_email}</div>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900">
                            {booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : '-'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm text-gray-900 break-words">{booking.slot_time ? formatSlotTime(booking.slot_time) : '-'}</div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="text-xs sm:text-sm font-semibold text-gray-900">₹{booking.total_amount}</div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            booking.refund_status === 'refunded'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {booking.refund_status === 'refunded' ? (
                              <CheckCircleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            ) : (
                              <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            )}
                            {booking.refund_status === 'refunded' ? 'Refunded' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 text-[10px] sm:text-xs text-gray-500">
                          {new Date(booking.created_at).toLocaleString()}
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          {booking.refund_status !== 'refunded' && (
                            <button
                              onClick={() => markRefunded(booking.id)}
                              className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                              title="Mark as Refunded"
                            >
                              <BanknotesIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                              <span className="hidden sm:inline">Refund</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
        </div>
      </div>

      {/* Create Booking Modal */}
      {showCreateBooking && (
        <CreateBookingModal
          courts={courts}
          selectedDate={selectedDate}
          adminUser={user}
          onClose={() => setShowCreateBooking(false)}
          onSuccess={() => {
            setShowCreateBooking(false);
            fetchBookings();
          }}
          showToast={showToast}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Booking</h3>
              <p className="text-sm text-gray-500 mt-2">Are you sure you want to cancel this booking?</p>
            </div>

            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Choose cancellation type:</p>
              <div className="flex gap-3">
                <button
                  onClick={() => confirmCancelBooking(true)}
                  className="flex-1 px-4 py-3 bg-green-50 border-2 border-green-300 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium text-sm"
                >
                  Cancel & Refund
                </button>
                <button
                  onClick={() => confirmCancelBooking(false)}
                  className="flex-1 px-4 py-3 bg-red-50 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm"
                >
                  Cancel, No Refund
                </button>
              </div>
            </div>

            <button
              onClick={() => setCancelConfirm(null)}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      {statusConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Approve Booking</h3>
              <p className="text-sm text-gray-500 mt-2">Are you sure you want to approve this booking?</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStatusConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                No
              </button>
              <button
                onClick={confirmStatusUpdate}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                Yes, Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Slot Confirmation Modal */}
      {removeSlotConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove Time Slot</h3>
              <p className="text-sm text-gray-500 mt-2">
                Remove <span className="font-medium text-gray-700">{formatSlotTime(removeSlotConfirm.slot_time)}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRemoveSlotConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveSlot}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBookingData && (
        <EditBookingModal
          booking={editingBookingData}
          courts={courts}
          onClose={() => setEditingBookingData(null)}
          onSuccess={() => {
            setEditingBookingData(null);
            fetchBookings();
          }}
          showToast={showToast}
        />
      )}

      {/* Create Announcement Modal */}
      {showCreateAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Create Announcement</h2>
                <button
                  onClick={() => {
                    setShowCreateAnnouncement(false);
                    setNewAnnouncement('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Announcement Content
                </label>
                <textarea
                  value={newAnnouncement}
                  onChange={(e) => setNewAnnouncement(e.target.value)}
                  placeholder="Enter your announcement message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateAnnouncement(false);
                    setNewAnnouncement('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createAnnouncement}
                  disabled={!newAnnouncement.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Announcement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Slot Modal */}
      {showAddSlotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Add Time Slot</h2>
              <button onClick={() => setShowAddSlotModal(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <div className="flex items-center gap-2">
                  <select
                    value={newSlotHour}
                    onChange={(e) => setNewSlotHour(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                      <option key={h} value={String(h)}>{h}</option>
                    ))}
                  </select>
                  <span className="text-gray-500 font-bold">:</span>
                  <select
                    value={newSlotMinute}
                    onChange={(e) => setNewSlotMinute(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="00">00</option>
                    <option value="30">30</option>
                  </select>
                  <select
                    value={newSlotPeriod}
                    onChange={(e) => setNewSlotPeriod(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newSlotType}
                  onChange={(e) => setNewSlotType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="members_only">Members Only</option>
                  <option value="available">Available</option>
                  <option value="coaching_hour">Coaching Hour</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddSlotModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSlot}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Add Slot
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Slot Modal */}
      {editingSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Edit Time Slot</h2>
              <button onClick={() => setEditingSlot(null)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <p className="text-gray-900 font-medium">{formatSlotTime(editingSlot.slot_time)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newSlotType}
                  onChange={(e) => setNewSlotType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="members_only">Members Only</option>
                  <option value="available">Available</option>
                  <option value="coaching_hour">Coaching Hour</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditingSlot(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateSlot}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Filter Modal */}
      {showExportFilter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Export Date Filter</h2>
                <button
                  onClick={() => setShowExportFilter(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Choose a date range or quick option. One option is mandatory before export.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    const filter = { type: 'today', from: '', to: '' };
                    setExportFilter(filter);
                    handleExport(filter);
                    setShowExportFilter(false);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-left hover:bg-gray-50"
                >
                  Today
                </button>

                <button
                  onClick={() => {
                    const filter = { type: 'yesterday', from: '', to: '' };
                    setExportFilter(filter);
                    handleExport(filter);
                    setShowExportFilter(false);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-left hover:bg-gray-50"
                >
                  Yesterday
                </button>

                <div className="border border-gray-200 rounded-md p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Custom Range</p>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <input
                        type="date"
                        value={exportFilter.type === 'range' ? exportFilter.from : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExportFilter((prev) => ({
                            type: 'range',
                            from: value,
                            to: prev.to,
                          }));
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <input
                        type="date"
                        value={exportFilter.type === 'range' ? exportFilter.to : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExportFilter((prev) => ({
                            type: 'range',
                            from: prev.from,
                            to: value,
                          }));
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Both from and to dates are required for range export.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-2 border-t">
                <button
                  onClick={() => setShowExportFilter(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    if (exportFilter.type !== 'range') {
                      showToast('Select From and To dates for range export.', 'error');
                      return;
                    }
                    if (!exportFilter.from || !exportFilter.to) {
                      showToast('Please select both From and To dates.', 'error');
                      return;
                    }
                    handleExport(exportFilter);
                    setShowExportFilter(false);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function EditBookingModal({ booking, courts, onClose, onSuccess, showToast }) {
  const [selectedCourt, setSelectedCourt] = useState(
    courts.find(c => c.id === booking.court_id) || courts[0] || null
  );
  const [bookingDate, setBookingDate] = useState(
    booking.booking_date ? new Date(booking.booking_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [availability, setAvailability] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const normalizeTime = (t) => (t || '').substring(0, 5);

  const getCurrentBookingSlotTimes = () => {
    if (booking.slot_time) {
      return [normalizeTime(booking.slot_time)];
    }
    if (booking.time_slots) {
      const parseFormatted = (formatted) => {
        const match = formatted.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return null;
        let hour = parseInt(match[1]);
        const minute = match[2];
        const period = match[3].toUpperCase();
        if (period === 'AM' && hour === 12) hour = 0;
        else if (period === 'PM' && hour !== 12) hour += 12;
        return `${hour.toString().padStart(2, '0')}:${minute}`;
      };
      return booking.time_slots.split(',').map(s => parseFormatted(s)).filter(Boolean);
    }
    return [];
  };

  const fetchAvailability = useCallback(async () => {
    const { data, error } = await database.getAvailability(bookingDate, selectedCourt.id);

    if (!error) {
      const currentSlotTimes = getCurrentBookingSlotTimes();
      const isOriginalCourtAndDate =
        selectedCourt.id === (courts.find(c => c.id === booking.court_id)?.id) &&
        bookingDate === (booking.booking_date ? new Date(booking.booking_date).toISOString().split('T')[0] : '');

      const updatedData = (data || []).map(slot => {
        const slotTimeNorm = (slot.slot_time || '').substring(0, 5);
        const isCurrentSlot = isOriginalCourtAndDate && currentSlotTimes.includes(slotTimeNorm);
        return {
          ...slot,
          is_available: slot.is_available || isCurrentSlot,
          _isCurrentBooking: isCurrentSlot
        };
      });

      setAvailability(updatedData);

      if (initialLoad && isOriginalCourtAndDate) {
        const preSelected = updatedData.filter(s => s._isCurrentBooking);
        setSelectedSlots(preSelected);
        setInitialLoad(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDate, selectedCourt]);

  useEffect(() => {
    if (selectedCourt) {
      fetchAvailability();
    }
  }, [selectedCourt, bookingDate, fetchAvailability]);

  const originalSlotCount = getCurrentBookingSlotTimes().length || 1;

  const handleSlotToggle = (slot) => {
    if (!slot.is_available) return;

    setSelectedSlots(prev => {
      const isSelected = prev.some(s => s.slot_time === slot.slot_time);
      if (isSelected) {
        return prev.filter(s => s.slot_time !== slot.slot_time);
      } else if (prev.length < originalSlotCount) {
        return [...prev, slot];
      } else {
        // Replace the oldest selected slot with the new one
        return [...prev.slice(1), slot];
      }
    });
  };

  const handleUpdateBooking = async () => {
    if (selectedSlots.length === 0) {
      showToast('Please select at least one time slot', 'error');
      return;
    }

    if (selectedSlots.length !== originalSlotCount) {
      showToast(`Please select exactly ${originalSlotCount} slot${originalSlotCount > 1 ? 's' : ''} (same as original booking)`, 'error');
      return;
    }

    setLoading(true);

    try {
      const bookingId = booking.booking_id || booking.id;
      const slotTimes = selectedSlots.map(slot => slot.slot_time);

      const { error } = await supabase
        .from('bookings')
        .update({
          court_id: selectedCourt.id,
          booking_date: bookingDate,
          slot_time: slotTimes[0]
        })
        .eq('id', bookingId);

      if (error) throw error;

      showToast('Booking updated successfully!', 'success');
      onSuccess();
    } catch (error) {
      console.error('Update booking error:', error);
      showToast('Failed to update booking: ' + error.message, 'error');
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Edit Booking</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-3"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Customer Info (read-only) */}
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Customer</h3>
                <p className="text-base sm:text-lg font-semibold text-gray-900">{booking.customer_name}</p>
                <p className="text-sm text-gray-600">{booking.customer_phone}</p>
              </div>
              <div className="text-right flex-shrink-0 ml-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  booking.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {booking.status}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="text-xs sm:text-sm font-medium text-gray-500">Current Slot:</span>
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-lg bg-blue-100 text-blue-800 text-xs sm:text-sm font-semibold">
                🕐 {booking.slot_time ? formatTimeUtil(booking.slot_time) : (booking.time_slots || '-')}
              </span>
              <span className="text-xs sm:text-sm text-gray-500">
                📅 {booking.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '-'}
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
            {/* Left: Date & Court */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Booking Date</label>
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => {
                    setBookingDate(e.target.value);
                    setSelectedSlots([]);
                    setInitialLoad(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mt-2">
                  {new Date(bookingDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Court</label>
                <div className="space-y-2">
                  {courts.map(court => (
                    <div
                      key={court.id}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                        selectedCourt?.id === court.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                      onClick={() => {
                        setSelectedCourt(court);
                        setSelectedSlots([]);
                        setInitialLoad(false);
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm sm:text-base">{court.name}</span>
                        <span className="text-green-600 font-semibold text-sm">₹{court.hourly_rate}/hr</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Slots Summary */}
              <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-green-900 text-sm sm:text-base">Selected Slots</h4>
                  <span className="text-xs text-gray-500">{selectedSlots.length}/{originalSlotCount} selected</span>
                </div>
                {selectedSlots.length > 0 ? (
                  <div className="space-y-1.5">
                    {selectedSlots.map(slot => (
                      <div key={slot.slot_time} className="flex justify-between items-center bg-white p-2 rounded-lg">
                        <span className="font-medium text-sm">{formatTimeUtil(slot.slot_time)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Select {originalSlotCount} slot{originalSlotCount > 1 ? 's' : ''}</p>
                )}
                <div className="border-t border-green-200 mt-3 pt-3">
                  <p className="text-xs text-gray-500">Already paid: ₹{booking.total_amount}</p>
                </div>
              </div>
            </div>

            {/* Right: Time Slots */}
            <div className="lg:col-span-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Available Time Slots</h3>
              <TimeSlotGrid
                availability={availability}
                selectedSlots={selectedSlots}
                bookingDate={bookingDate}
                onSlotToggle={handleSlotToggle}
                currentBookingSlots={getCurrentBookingSlotTimes()}
              />
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateBooking}
            disabled={loading || selectedSlots.length !== originalSlotCount}
            className="px-4 sm:px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                Updating...
              </div>
            ) : (
              'Update Booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Enhanced Create Booking Modal Component with Green Theme
function CreateBookingModal({ courts, selectedDate, adminUser, onClose, onSuccess, showToast }) {
  const [selectedCourt, setSelectedCourt] = useState(courts[0] || null);
  const [bookingDate, setBookingDate] = useState(selectedDate);
  const [availability, setAvailability] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [phoneLookupLoading, setPhoneLookupLoading] = useState(false);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const phoneLookupGen = useRef(0);
  const phoneFieldWrapRef = useRef(null);

  const fetchAvailability = useCallback(async () => {
    const { data, error } = await database.getAvailability(bookingDate, selectedCourt.id);

    if (!error) {
      setAvailability(data || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDate, selectedCourt]);

  useEffect(() => {
    if (selectedCourt) {
      fetchAvailability();
    }
  }, [selectedCourt, bookingDate, fetchAvailability]);

  useEffect(() => {
    const digits = customerInfo.phone.replace(/\D/g, '');
    if (digits.length < 3) {
      setPhoneLookupLoading(false);
      setPhoneSuggestions([]);
      return;
    }

    const gen = ++phoneLookupGen.current;
    const timer = setTimeout(async () => {
      setPhoneLookupLoading(true);
      try {
        const { data, error } = await supabase.rpc('list_customers_by_phone_prefix', {
          p_phone: digits,
          p_limit: 25
        });
        if (gen !== phoneLookupGen.current) return;
        if (error) {
          setPhoneSuggestions([]);
          return;
        }
        let rows = [];
        if (Array.isArray(data)) rows = data;
        else if (typeof data === 'string') {
          try {
            const parsed = JSON.parse(data);
            rows = Array.isArray(parsed) ? parsed : [];
          } catch {
            rows = [];
          }
        }
        setPhoneSuggestions(rows);
      } finally {
        if (gen === phoneLookupGen.current) setPhoneLookupLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [customerInfo.phone]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (phoneFieldWrapRef.current && !phoneFieldWrapRef.current.contains(e.target)) {
        setPhoneFieldFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const pickCustomerFromList = (row) => {
    const phone = row.phone != null ? String(row.phone).replace(/\D/g, '').slice(0, 10) : '';
    const name = row.full_name != null ? String(row.full_name).trim() : '';
    const email = row.email != null ? String(row.email).trim() : '';
    setCustomerInfo({ phone, name, email });
    setPhoneSuggestions([]);
    setPhoneFieldFocused(false);
  };

  const handleSlotToggle = (slot) => {
    const isBookableType = slot.slot_type_label === 'available' || !slot.slot_type_label;
    if (!slot.is_available || !isBookableType) return;

    setSelectedSlots(prev => {
      const isSelected = prev.some(s => s.slot_time === slot.slot_time);
      if (isSelected) {
        return prev.filter(s => s.slot_time !== slot.slot_time);
      } else {
        return [...prev, slot];
      }
    });
  };

  const handleCreateBooking = async () => {
    if (selectedSlots.length === 0) {
      showToast('Please select at least one time slot', 'error');
      return;
    }

    if (!customerInfo.name || !customerInfo.phone) {
      showToast('Please fill in customer name and phone', 'error');
      return;
    }

    const phonePattern = /^[6-9][0-9]{9}$/;
    if (!phonePattern.test(customerInfo.phone)) {
      showToast('Please enter a valid 10-digit Indian phone number starting with 6, 7, 8, or 9', 'error');
      return;
    }

    setLoading(true);

    try {
      const totalAmount = selectedSlots.length * selectedCourt.hourly_rate;
      
      // Use atomic booking function to prevent race conditions
      const slotTimes = selectedSlots.map(slot => slot.slot_time);
      
      const { data, error } = await supabase
        .rpc('create_booking_atomic', {
          p_court_id: selectedCourt.id,
          p_booking_date: bookingDate,
          p_customer_name: customerInfo.name,
          p_customer_phone: customerInfo.phone,
          p_customer_email: customerInfo.email || null,
          p_selected_slots: slotTimes,
          p_total_amount: totalAmount,
          p_user_id: null // Always null for admin-created guest bookings
        });

      if (error) throw error;

      const result = data[0];

      if (!result.success) {
        showToast(result.message, 'error');
        return;
      }

      // Auto-approve admin-created bookings
      if (result.booking_id && adminUser?.id) {
        try {
          const { error: approvalError } = await supabase.rpc('approve_booking', {
            p_booking_id: result.booking_id,
            p_admin_user_id: adminUser.id
          });

          if (approvalError) {
            console.error('Auto-approval error:', approvalError);
          } else {
            console.log('Admin booking auto-approved');
            notifyBookingApproved({ booking_id: result.booking_id });
          }
        } catch (autoApprovalError) {
          console.error('Auto-approval failed:', autoApprovalError);
        }
      }

      // Show appropriate message based on booking result
      let message = `Admin booking created and approved successfully!\nCustomer: ${customerInfo.name}\n`;
      
      if (result.failed_slots && result.failed_slots.length > 0) {
        const successfulSlots = selectedSlots.length - result.failed_slots.length;
        message += `Slots: ${successfulSlots}/${selectedSlots.length} (Some slots were already booked)\n`;
        message += `Total: ₹${(totalAmount * successfulSlots / selectedSlots.length)}`;
      } else {
        message += `Slots: ${selectedSlots.length}\nTotal: ₹${totalAmount}`;
      }
      
      showToast(message, 'success');
      onSuccess();

    } catch (error) {
      console.error('Complete error details:', error);
      showToast('Failed to create booking: ' + error.message, 'error');
    }

    setLoading(false);
  };

  const totalAmount = selectedSlots.length * (selectedCourt?.hourly_rate || 300);
  const phoneDigits = customerInfo.phone.replace(/\D/g, '');
  const showPhoneDropdown =
    phoneFieldFocused && phoneDigits.length >= 3 && (phoneSuggestions.length > 0 || phoneLookupLoading);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Create Admin Booking</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-3"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="space-y-5">
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Booking Date
                </label>
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => {
                    setBookingDate(e.target.value);
                    setSelectedSlots([]);
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Court Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Court
                </label>
                <div className="space-y-2">
                  {courts.map(court => (
                    <div
                      key={court.id}
                      className={`border-2 rounded-lg p-3 sm:p-4 cursor-pointer transition-all ${
                        selectedCourt?.id === court.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                      onClick={() => setSelectedCourt(court)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm sm:text-base">{court.name}</span>
                        <span className="text-green-600 font-semibold text-sm">₹{court.hourly_rate}/hr</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="space-y-3">
                  <div ref={phoneFieldWrapRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            setCustomerInfo(prev => ({ ...prev, phone: value }));
                          }
                        }}
                        onFocus={() => setPhoneFieldFocused(true)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Type at least 3 digits, then pick a member"
                        pattern="[6-9][0-9]{9}"
                        maxLength="10"
                        autoComplete="tel"
                      />
                      {showPhoneDropdown && (
                      <ul
                        className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                        role="listbox"
                      >
                        {phoneLookupLoading && phoneSuggestions.length === 0 && (
                          <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
                        )}
                        {!phoneLookupLoading && phoneSuggestions.length === 0 && (
                          <li className="px-3 py-2 text-sm text-gray-500">No registered member matches this number.</li>
                        )}
                        {phoneSuggestions.map((row) => (
                          <li key={row.phone}>
                            <button
                              type="button"
                              role="option"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 focus:bg-green-50 focus:outline-none"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickCustomerFromList(row)}
                            >
                              <span className="font-medium text-gray-900">{row.phone}</span>
                              <span className="mx-1.5 text-gray-300">·</span>
                              <span className="text-gray-700">{row.full_name || '—'}</span>
                              {row.email ? (
                                <span className="block truncate text-xs text-gray-500">{row.email}</span>
                              ) : null}
                            </button>
                          </li>
                        ))}
                      </ul>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      After 3 digits, matching registered members appear under the field — select one to fill phone, name, and email.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Enter customer name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Enter email (optional)"
                      autoComplete="email"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-5">
              {/* Date Display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Date
                </label>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm sm:text-lg font-semibold text-gray-900">
                    {new Date(bookingDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Available Time Slots</h3>
                <TimeSlotGrid
                  availability={availability}
                  selectedSlots={selectedSlots}
                  bookingDate={bookingDate}
                  onSlotToggle={handleSlotToggle}
                />
              </div>

              {/* Selected Slots Summary */}
              {selectedSlots.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-green-900 mb-2 text-sm sm:text-base">Selected Slots:</h4>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                    {selectedSlots.map(slot => (
                      <span key={slot.slot_time} className="px-2 py-1 bg-green-500 text-white rounded text-xs">
                        {formatTimeUtil(slot.slot_time)}
                      </span>
                    ))}
                  </div>
                  <div className="text-base sm:text-lg font-bold text-green-700">
                    Total: ₹{totalAmount} ({selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''})
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateBooking}
            disabled={loading || selectedSlots.length === 0}
            className="px-4 sm:px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                Creating...
              </div>
            ) : (
              `Create Booking - ₹${totalAmount}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GreenAdminDashboard;